/**
 * focusRing — 조립 창 「지면으로 데려가기」의 초록 테두리를 **언제 켜고 언제 끄는지**만 맡는다(React 없음).
 *
 * 담은 목록의 행(또는 순번)을 누르면 지면 칸이 그 문항으로 굴러가고, 도착한 자리에 잠깐 테두리를 얹는다
 * (PaperComposeModal.focusOnPaper 가 부른다). 문항 요소는 PrintSheetV4 가 그린 것이라 React 로 클래스를 줄 수 없어
 * DOM 에 직접 얹는다 — 그래서 켜고 끄는 때를 이 모듈이 따로 들고 있다.
 *
 * 왜 따로 빼서 「멈춘 뒤부터」 세는가(R8 실측):
 * - 부드러운 스크롤은 거리에 비례해 길어져 최대 약 1.47초 걸린다(60문항 지면 끝까지 16,597px).
 * - 테두리 1,400ms 를 누르는 순간부터 세면 약 7,000px 부터는 도착하기 전에 꺼진다.
 * - 1.4초 안에 지면이 다시 배치되면(본문 묶음 도착·늦은 그림 재측정) 끝 문항이 다른 면·단으로 옮겨진다.
 *   PrintSheetV4 의 key 가 면(p-i) > 단(col) > 문항(problem_id) 이라 React 가 요소를 새로 만들고,
 *   DOM 에 직접 얹은 클래스가 사라지며 스크롤도 옛 자리에 멈춘다 → retarget 이 같은 번호를 다시 찾는다.
 * - 같은 문항을 1.4초 안에 다시 누르면 즉시 꺼졌다(새 요소에 붙인 뒤 「이전 요소」에서 떼는데 같은 요소였다).
 *
 * 클래스는 ring-*(box-shadow) 라 조판을 밀지 않고, 교체 대상 테두리(outline-*)와도 안 겹친다.
 */

export const FOCUS_RING_CLASSES = ['ring-2', 'ring-primary', 'ring-offset-2'];
/** 도착한 뒤 테두리를 보여 주는 시간 */
export const FOCUS_RING_HOLD_MS = 1400;
/** 지면 칸의 scroll 이벤트가 이만큼 조용하면 멈췄다고 본다 */
export const SCROLL_IDLE_MS = 100;
/** 누른 뒤 이만큼 지나도 멈춤이 안 잡히면 그때부터 센다(상한) */
export const SCROLL_SETTLE_CAP_MS = 2000;
/** 움직일 거리가 이보다 작으면 스크롤을 기다리지 않는다 */
const STILL_PX = 1;

const itemSelector = (number) => `.pv4-problem-item[data-problem-number="${number}"]`;

/**
 * 지면 칸(box) 안에서 el 을 세로 가운데로 두는 스크롤 위치. 칸보다 긴 문항은 시작 부분을 맞춘다.
 * 배율을 적용한 실제 좌표를 사용한다. 가로는 벗어났을 때만 왼쪽 끝을 맞춘다.
 * 두 상자의 거리는 서랍이 어디까지 미끄러졌든 같다 — 둘이 같이 움직인다.
 */
export function centerTarget(box, el) {
  const b = box.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  const top = Math.max(0, box.scrollTop + (r.top - b.top - box.clientTop) - Math.max(0, (box.clientHeight - r.height) / 2));
  const left = (r.left < b.left || r.right > b.right) ? Math.max(0, box.scrollLeft + (r.left - b.left)) : box.scrollLeft;
  return { top, left };
}

/**
 * 지면 칸(box) 안에서만 굴린다 — scrollIntoView 는 조상 스크롤을 전부 굴린다. 서랍이 닫혀(오른쪽 밖으로 비켜서) 있으면
 * 가로 overflow-hidden 인 열 상자까지 옆으로 밀어, 서랍이 [지면] 버튼의 0.3초 미끄럼이 아니라 스크롤 속도로
 * 튀어나왔다(오너 2026-09-14 「속도를 버튼 누를 때로」). 서랍은 제 transform 으로만 열리게 둔다.
 */
export function scrollBoxTo(box, { top, left }, behavior) {
  if (typeof box.scrollTo === 'function') box.scrollTo({ top, left, behavior });
  else { box.scrollTop = top; box.scrollLeft = left; }
}

/** 실제로 굴러갈 거리(px) — 목표를 굴릴 수 있는 범위로 자른 뒤 지금 위치와 잰다. 끝 문항은 가운데까지 못 온다 */
function travel(box, { top, left }) {
  const maxTop = Math.max(0, (box.scrollHeight || 0) - (box.clientHeight || 0));
  const maxLeft = Math.max(0, (box.scrollWidth || 0) - (box.clientWidth || 0));
  return Math.max(
    Math.abs(Math.min(top, maxTop) - box.scrollTop),
    Math.abs(Math.min(left, maxLeft) - box.scrollLeft),
  );
}

/**
 * 테두리 하나를 다루는 손잡이. 한 창에 하나 만들어 두고 focus · retarget · stop 만 부른다.
 * 들고 있는 것: 번호(data-problem-number) · 요소 · 상자 · 타이머(멈춤 대기·상한·보여 주기) · scroll 리스너.
 */
export function createFocusRing() {
  let cur = null;

  /** 멈춤 대기(리스너·조용함 타이머·상한 타이머)와 보여 주기 타이머를 끊는다. 클래스는 건드리지 않는다 */
  const disarm = (s) => {
    clearTimeout(s.idle);
    clearTimeout(s.cap);
    clearTimeout(s.hold);
    s.idle = 0; s.cap = 0; s.hold = 0;
    if (s.onScroll) {
      s.box.removeEventListener('scroll', s.onScroll);
      s.onScroll = null;
    }
  };

  /** 멈췄다 — 여기서부터 1,400ms 를 센다 */
  const startHold = (s) => {
    if (cur !== s) return;
    disarm(s);
    s.hold = setTimeout(() => {
      if (cur !== s) return;
      s.el.classList.remove(...FOCUS_RING_CLASSES);
      cur = null;
    }, FOCUS_RING_HOLD_MS);
  };

  /** 굴러가는 중 — scroll 이 100ms 조용해지거나 누른 뒤 2,000ms 가 지나면 센다 */
  const waitSettle = (s) => {
    s.onScroll = () => {
      clearTimeout(s.idle);
      s.idle = setTimeout(() => startHold(s), SCROLL_IDLE_MS);
    };
    s.box.addEventListener('scroll', s.onScroll, { passive: true });
    s.cap = setTimeout(() => startHold(s), SCROLL_SETTLE_CAP_MS);
  };

  /**
   * el 로 굴리고 테두리를 얹는다. behavior 는 부르는 쪽이 정한다(움직임 줄이기면 'auto').
   * 같은 요소를 다시 누르면 클래스를 떼지 않고 기다림만 처음부터 다시 한다. 다른 요소면 옛 요소의 테두리를 뗀다.
   */
  const focus = ({ box, el, behavior = 'smooth' }) => {
    const target = centerTarget(box, el);
    const moving = travel(box, target) >= STILL_PX; // 굴리기 전에 잰다 — 'auto' 는 그 자리에서 끝난다
    scrollBoxTo(box, target, behavior);
    if (cur != null) {
      disarm(cur);
      if (cur.el !== el) cur.el.classList.remove(...FOCUS_RING_CLASSES);
    }
    el.classList.add(...FOCUS_RING_CLASSES);
    const s = { el, box, number: el.getAttribute('data-problem-number'), idle: 0, cap: 0, hold: 0, onScroll: null };
    cur = s;
    if (moving) waitSettle(s);
    else startHold(s);
  };

  /** 다음 포커스 전·창 닫힘·언마운트 — 타이머·리스너를 다 끊고 테두리를 뗀다 */
  const stop = () => {
    if (cur == null) return;
    disarm(cur);
    cur.el.classList.remove(...FOCUS_RING_CLASSES);
    cur = null;
  };

  /**
   * 지면이 다시 배치된 뒤 부른다. 테두리가 살아 있는데 요소가 문서에서 떨어졌으면 같은 번호를 다시 찾아
   * 테두리를 얹고 그 자리로 **즉시** 가운데 맞춘 뒤 1,400ms 를 다시 센다. 못 찾으면(문항이 빠짐) 내려놓는다.
   * 요소가 그대로면 아무것도 안 한다.
   */
  const retarget = (box) => {
    const s = cur;
    if (s == null || s.el.isConnected) return;
    const next = box != null && s.number != null ? box.querySelector(itemSelector(s.number)) : null;
    if (next == null) { stop(); return; }
    disarm(s);
    next.classList.add(...FOCUS_RING_CLASSES);
    s.el = next;
    s.box = box;
    scrollBoxTo(box, centerTarget(box, next), 'auto');
    startHold(s);
  };

  /** 테스트용 — 지금 테두리를 얹은 요소와 번호 */
  const current = () => (cur == null ? null : { el: cur.el, number: cur.number });

  return { focus, retarget, stop, current };
}
